import React, { FC } from 'react';
import { Box, useTheme } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { isEmpty } from 'lodash';

import { Typography } from '../Typography/Typography';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { QuantitySelectorV2 } from '../MenuSection/QuantitySelectorV2';

interface DialogFooterProps {
  selectedCount: number;
  requiredCount: number;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onAddToCart: () => void;
  isMealKit: boolean;
  maxQuantity: number;
  isEntree: boolean;
}

export const DialogFooter: FC<DialogFooterProps> = ({
  selectedCount,
  requiredCount,
  quantity,
  onQuantityChange,
  onAddToCart,
  isMealKit,
  maxQuantity = 100,
  isEntree,
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const usesI18n = !isEmpty(Object.keys(i18n));

  const getAddButtonText = () => {
    return isMealKit
      ? 'Add Meal Kit'
      : isEntree
      ? 'Add Prepared Meal'
      : 'Add Snack/Side';
  };

  const selectionCountTranslationKey = isEntree
    ? 'meals:dishDetails.selectionCount.entree'
    : 'meals:dishDetails.selectionCount.snackSide';

  return (
    <Box
      p={3}
      borderTop={1}
      borderColor="divider"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      bgcolor="background.paper"
      borderRadius={`0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        i18nKey={selectionCountTranslationKey}
        i18nParams={{
          selectedCount,
          requiredCount,
          sectionType: isEntree ? 'entreeDishes' : 'snacks',
        }}
      >
        {usesI18n ? (
          <Trans
            i18nKey={selectionCountTranslationKey}
            values={{ selectedCount, requiredCount }}
            components={[<Box component="span" sx={{ fontWeight: 600 }} />]}
          />
        ) : (
          <>
            You have selected{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {selectedCount} out of {requiredCount}
            </Box>{' '}
            {isEntree ? 'entree dishes' : 'snacks and sides'}
          </>
        )}
      </Typography>

      <Box display="flex" alignItems="center" gap={2}>
        <Box>
          <QuantitySelectorV2
            value={quantity}
            onChange={onQuantityChange}
            isDisabled={false}
            isDecrementDisabled={quantity <= 0}
            isIncrementDisabled={quantity >= maxQuantity}
            toggleDecrementIcon={false}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={onAddToCart}
          startIcon={
            <Icon
              name="shopping-bag-outline"
              fill={theme.palette.primary.contrastText}
              size="large"
            />
          }
          sx={{
            width: '251px',
          }}
          disabled={quantity <= 0 || quantity > maxQuantity}
        >
          <Typography
            variant="button"
            i18nKey={
              isMealKit
                ? 'meals:dishDetails.addButtons.mealKit'
                : isEntree
                ? 'meals:dishDetails.addButtons.preparedMeal'
                : 'meals:dishDetails.addButtons.snackSide'
            }
          >
            {getAddButtonText()}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

export default DialogFooter;
